import { computed, onMounted, ref, watch, type Ref } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useToast } from 'vue-toastification';
import getAccountShipmentRoutesBatch from '@gpx-spa/queries/src/getAccountShipmentRoutesBatch';
import type getAccountShipmentV2 from '@gpx-spa/queries/src/getAccountShipmentV2';
import { useShipmentFormStore } from '@/stores/useShipmentFormStore';
import { hydrateFormStoreFromShipment } from '@/composables/useShipmentFormHydrate';
import { useDevice } from '@/composables/useDevice';
import { canEditShipmentForUser } from '@/utils/shipment-draft-access';
import type { ShipmentEditCapabilities } from '@/components/shipment/edit/utils/shipment-edit-capabilities';

export function useShipmentEditHydration(options: {
  shipmentId: Ref<number>;
  shipmentQuery: ReturnType<typeof getAccountShipmentV2>;
  capabilities: Ref<ShipmentEditCapabilities>;
  accountId: Ref<number>;
  initDeviceValueFromShipment: (ids: readonly number[]) => void;
}) {
  const vuexStore = useStore();
  const router = useRouter();
  const toast = useToast();
  const formStore = useShipmentFormStore();
  const deviceStore = useDevice();

  const hydrated = ref(false);
  const shipment = computed(() => options.shipmentQuery.data.value ?? null);

  const routeBatchQuery = getAccountShipmentRoutesBatch(
    options.accountId,
    computed(() => [options.shipmentId.value]),
    { enabled: computed(() => Boolean(shipment.value)) },
  );

  const routesHydrationReady = computed(() => routeBatchQuery.isFetched.value);

  function guardAccess() {
    const row = shipment.value;
    if (!row) return;
    if (
      !canEditShipmentForUser(
        row as Parameters<typeof canEditShipmentForUser>[0],
        true,
      )
    ) {
      toast.info('You do not have permission to edit this shipment.');
      void router.replace({
        name: 'ShipmentDetailsView',
        params: { id: String(row.id) },
      });
      return;
    }
    if (!options.capabilities.value.canAccessPage) {
      toast.info('This shipment cannot be edited.');
      void router.replace({
        name: 'ShipmentDetailsView',
        params: { id: String(row.id) },
      });
    }
  }

  watch(
    () => options.shipmentId.value,
    () => {
      hydrated.value = false;
      formStore.resetForm();
    },
    { immediate: true },
  );

  watch(
    [shipment, routesHydrationReady, () => routeBatchQuery.data.value],
    ([row, routesReady, batch]) => {
      if (!row || !routesReady) return;
      if (formStore.formHydrationSuspended) return;
      const geofences = vuexStore.getters['geofence/all'] ?? [];
      const routeBatchRows = batch ?? [];

      if (!hydrated.value) {
        hydrateFormStoreFromShipment(formStore, row, {
          geofences,
          routeBatchRows,
        });
        formStore.markDeviceIdsPersisted(row.device_ids ?? []);
        options.initDeviceValueFromShipment(row.device_ids ?? []);
        hydrated.value = true;
        guardAccess();
      }
    },
    { immediate: true },
  );

  onMounted(() => {
    hydrated.value = false;
    vuexStore.dispatch('geofence/fetch');
    void deviceStore.fetchByAccountId(options.accountId.value, {
      subscription_status: 'active',
      device_status: 'active,provisioned',
    });
  });

  watch(shipment, () => {
    if (shipment.value && hydrated.value) {
      guardAccess();
    }
  });

  return {
    hydrated,
    routesHydrationReady,
    routeBatchQuery,
  };
}
